import React, { useEffect, useState } from 'react';
import { FaDownload, FaLeaf, FaLock, FaSpinner } from 'react-icons/fa';
import { ContentSection } from '../../components/Layout/ContentSection';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import { useMutation } from '@apollo/client';
import { updateStoreSuperAdminMutation } from '../../graphql/mutations';
import { Communicator } from '../../tools/Communication';

interface SustainabilityReport {
  id: string;
  year: number;
  month: number;
  storeId: string;
  createdAt: string;
  totalContainersReused: number | null;
  co2SavedKg: number | null;
}

interface DownloadResponse {
  downloadUrl: string;
  fileName: string;
}

type SustainabilityReportsTabProps = {
  vytalStoreId: string;
  title: string;
  isEligible: boolean;
  // Both only on the admin store page: they render the send-reports toggle.
  storePickpackId?: string;
  sendReports?: boolean;
};

export const SustainabilityReportsTab = ({
  vytalStoreId,
  title,
  isEligible,
  storePickpackId,
  sendReports,
}: SustainabilityReportsTabProps) => {
  const CONTACT_WEBSITE_URL = 'https://www.vytal.org/de/kontakt';
  const [reports, setReports] = useState<SustainabilityReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(
    null,
  );

  const hasAccessToSustainabilityReports = isEligible;

  const [updateStore, { loading: savingSendReports }] = useMutation(
    updateStoreSuperAdminMutation,
  );
  const toggleSendReports = (enabled: boolean) =>
    updateStore({
      variables: {
        id: storePickpackId,
        store: { sustainability_reports_enabled: enabled },
      },
    });

  useEffect(() => {
    if (vytalStoreId && hasAccessToSustainabilityReports) {
      loadReports();
    }
  }, [vytalStoreId, hasAccessToSustainabilityReports]);

  async function loadReports() {
    setLoading(true);
    setError(null);
    try {
      const response = await Communicator.Get(
        '/SustainabilityReports/GetReports',
        { storeId: vytalStoreId },
      );
      const data = (await response.json()) as SustainabilityReport[];
      setReports(data);
    } catch (e: any) {
      console.error('Error loading sustainability reports:', e);
      setError(e.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }

  const formatReportPeriod = (report: SustainabilityReport) => {
    const date = new Date(report.year, report.month - 1);
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
    });
  };

  const formatCreatedAt = (report: SustainabilityReport) => {
    return new Date(report.createdAt).toLocaleDateString(
      i18n.language,
      getDateFormatOptions(true),
    );
  };

  const formatCo2Saved = (report: SustainabilityReport) => {
    if (report.co2SavedKg == null) return '-';
    return `${report.co2SavedKg.toFixed(2)} kg`;
  };

  const handleDownload = async (report: SustainabilityReport) => {
    setDownloadingReportId(report.id);
    try {
      const response = await Communicator.Get(
        '/SustainabilityReports/GetDownloadUrl',
        { reportId: report.id },
      );
      const data = (await response.json()) as DownloadResponse;

      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      } else {
        setError('Report file not available for download');
      }
    } catch (e: any) {
      console.error('Error getting download URL:', e);
      setError(e.message || 'Failed to get download URL');
    } finally {
      setDownloadingReportId(null);
    }
  };

  const downloadButtonTemplate = (report: SustainabilityReport) => {
    const isDownloading = downloadingReportId === report.id;
    return (
      <Button
        color="primary"
        size="sm"
        onClick={() => handleDownload(report)}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <FaSpinner className="fa-spin" style={{ marginRight: '0.5rem' }} />
        ) : (
          <FaDownload style={{ marginRight: '0.5rem' }} />
        )}
        {i18n.t('SustainabilityReports:download')}
      </Button>
    );
  };

  if (!hasAccessToSustainabilityReports) {
    return (
      <ContentSection title={title} collapsable={false} key="content">
        <div
          style={{
            border: '1px solid #d1ecf1',
            backgroundColor: '#f4fbfd',
            borderRadius: '0.5rem',
            padding: '1rem',
          }}
        >
          <div className="d-flex align-items-center mb-2">
            <FaLock style={{ marginRight: '0.5rem', color: '#0c5460' }} />
            <strong style={{ color: '#0c5460' }}>
              {i18n.t('SustainabilityReports:softOptionTitle')}
            </strong>
          </div>
          <div style={{ color: '#0c5460', marginBottom: '0.75rem' }}>
            {i18n.t('SustainabilityReports:softOptionDescription')}
          </div>
          <Button
            color="secondary"
            size="sm"
            outline
            tag="a"
            href={CONTACT_WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {i18n.t('SustainabilityReports:softOptionCta')}
          </Button>
        </div>
      </ContentSection>
    );
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      {storePickpackId && (
        <FormGroup switch className="mb-3">
          <Input
            type="switch"
            id="send-sustainability-reports"
            checked={sendReports !== false}
            disabled={savingSendReports}
            onChange={(e) => toggleSendReports(e.target.checked)}
          />
          <Label check for="send-sustainability-reports">
            {i18n.t('SustainabilityReports:sendReports')}
          </Label>
          <div className="form-text">
            {i18n.t('SustainabilityReports:sendReportsHelp')}
          </div>
        </FormGroup>
      )}
      {sendReports === false && (
        <div className="alert alert-warning" role="alert">
          {i18n.t('SustainabilityReports:sendingDisabled')}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#e8f5e9',
          borderRadius: '0.25rem',
          border: '1px solid #c8e6c9',
        }}
      >
        <FaLeaf style={{ color: '#4caf50', fontSize: '1.5rem' }} />
        <span style={{ color: '#2e7d32' }}>
          {i18n.t('SustainabilityReports:description')}
        </span>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {i18n.t('SustainabilityReports:errorLoading')}: {error}
        </div>
      )}

      <DataTable
        value={reports}
        dataKey="id"
        loading={loading}
        emptyMessage={i18n.t('SustainabilityReports:noReportsAvailable')}
        className="p-datatable-striped"
        sortField="year"
        sortOrder={-1}
      >
        <Column
          field="period"
          header={i18n.t('SustainabilityReports:period')}
          body={formatReportPeriod}
          sortable
          sortField="year"
        />
        <Column
          field="totalContainersReused"
          header={i18n.t('SustainabilityReports:containersReused')}
          body={(report: SustainabilityReport) =>
            report.totalContainersReused?.toLocaleString() ?? '-'
          }
        />
        <Column
          field="co2SavedKg"
          header={i18n.t('SustainabilityReports:co2Saved')}
          body={formatCo2Saved}
        />
        <Column
          field="createdAt"
          header={i18n.t('SustainabilityReports:generatedAt')}
          body={formatCreatedAt}
        />
        <Column
          header={i18n.t('Common:actions')}
          body={downloadButtonTemplate}
          style={{ width: '150px' }}
        />
      </DataTable>
    </ContentSection>
  );
};

export default SustainabilityReportsTab;
